import React from 'react';
import {Box} from '@mui/material';
import {useSnackbar} from 'notistack';

import {ReportPackFormFragment} from '../../schema-types/ReportPackFormFragment';
import {FormTypes, ReportPackTemplateTypes} from '../../schema-types/globalTypes';
import {SaltusAny} from '../../utils/SaltusAny';
import {enumToLabel} from '../../utils/typescriptUtils';
import Theme from '../../theme/Theme';

import {useReportPack} from './useReportPack';
import {ReportPackAddFormItem} from './ReportPackAddFormItem';
import {ReportPackFragment} from '../../schema-types/ReportPackFragment';

interface AddFormType {
  fn: (args: any) => Promise<any>;
  title: string;
}

export const ReportPackAddFormList: React.FC<{
  contactCuroId: string;
  reportPackId: string;
  handleSuccess: (data: ReportPackFormFragment) => SaltusAny;
  templateType: ReportPackFragment['templateType'];
}> = ({reportPackId, handleSuccess, contactCuroId, templateType}) => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const {
    onUpsertISATransferForm,
    onUpsertIsaApplicationForm,
    onUpsertJISATransferForm,
    onUpsertJisaApplicationForm,
    onUpsertNomineeTransferForm,
    onUpsertSippContributionForm,
    onUpsertSippTransferForm,
    onUpsertSippDdmForm,
    onUpsertMultreesDdmForm,
    onUpsertExpressionOfWishesForm,
    onUpsertFurtherActionRequiredForm,
    onUpsertBankDetailCollectionForm,
  } = useReportPack();

  const mutationMap: Record<FormTypes, AddFormType> = {
    [FormTypes.ISA_TRANSFER]: {fn: onUpsertISATransferForm, title: 'ISA Transfer'},
    [FormTypes.JISA_TRANSFER]: {
      fn: onUpsertJISATransferForm,
      title: 'Junior ISA Transfer',
    },
    [FormTypes.ISA_APPLICATION]: {
      fn: onUpsertIsaApplicationForm,
      title: 'ISA Application',
    },
    [FormTypes.JISA_APPLICATION]: {
      fn: onUpsertJisaApplicationForm,
      title: 'JISA Application',
    },
    [FormTypes.NOMINEE_TRANSFER]: {
      fn: onUpsertNomineeTransferForm,
      title: 'Nominee Transfer',
    },
    [FormTypes.SIPP_CONTRIBUTION]: {
      fn: onUpsertSippContributionForm,
      title: 'SIPP Contribution',
    },
    [FormTypes.SIPP_TRANSFER]: {fn: onUpsertSippTransferForm, title: 'SIPP Transfer'},
    [FormTypes.SIPP_DDM]: {fn: onUpsertSippDdmForm, title: 'SIPP Direct Debit'},
    [FormTypes.EXPRESSION_OF_WISHES]: {
      fn: onUpsertExpressionOfWishesForm,
      title: 'Expression of Wishes',
    },
    [FormTypes.FURTHER_ACTION]: {
      fn: onUpsertFurtherActionRequiredForm,
      title: 'Further Action Required',
    },
    [FormTypes.BANK_DETAIL_COLLECTION]: {
      fn: onUpsertBankDetailCollectionForm,
      title: 'Bank Details Collection',
    },
    [FormTypes.MULTREES_DDM]: {
      fn: onUpsertMultreesDdmForm,
      title: 'Multrees Direct Debit',
    },
  };

  async function addForm(formType: FormTypes) {
    try {
      closeSnackbar();
      enqueueSnackbar({
        variant: 'info',
        message: `Creating ${enumToLabel(formType)} form...`,
        persist: true,
      });

      const mutationFn = mutationMap[formType].fn;

      if (!mutationFn) {
        throw new Error(`Invalid form type: ${formType}`);
      }

      const {data} = await mutationFn({
        variables: {
          reportPackId,
          contactCuroId,
        },
      });

      const formData = data?.[Object.keys(data)[0]]?.form;
      if (formData) {
        closeSnackbar();
        enqueueSnackbar({
          variant: 'success',
          message: `${enumToLabel(formType)} form created successfully`,
        });
        handleSuccess(formData);
      } else {
        throw data?.[Object.keys(data)[0]]?.errors;
      }
    } catch (err) {
      closeSnackbar();
      enqueueSnackbar({
        variant: 'error',
        message: `Unable to create ${enumToLabel(formType)} form`,
      });
    }
  }

  return (
    <Box
      sx={{
        backgroundColor: Theme.palette.background.default,
        borderRadius: '8px',
        padding: 3,
      }}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
        {Object.keys(mutationMap)
          .sort((a, b) => {
            const aKey = a as FormTypes;
            const bKey = b as FormTypes;
            if (mutationMap[aKey].title < mutationMap[bKey].title) {
              return -1;
            }
            if (mutationMap[aKey].title > mutationMap[bKey].title) {
              return 1;
            }
            return 0;
          })
          .filter((k) => {
            const key = k as FormTypes;
            if (
              templateType === ReportPackTemplateTypes.NO_ACTION_LETTER ||
              templateType === ReportPackTemplateTypes.DECLINED_LETTER ||
              templateType === ReportPackTemplateTypes.UNCONTACTABLE_LETTER
            ) {
              if (
                [
                  FormTypes.FURTHER_ACTION,
                  FormTypes.BANK_DETAIL_COLLECTION,
                  FormTypes.EXPRESSION_OF_WISHES,
                ].includes(key)
              ) {
                return true;
              }
              return false;
            }
            return true;
          })
          .map((k) => {
            const key = k as FormTypes;
            return (
              <ReportPackAddFormItem
                key={k}
                onAdd={() => addForm(key)}
                title={mutationMap[key].title}
              />
            );
          })}
      </Box>
    </Box>
  );
};
